import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { clsx } from 'clsx';
import React, { useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import type { TimeCardDetail, TimeCardShift, TimeCardShiftCreate } from '../../../../../js/api';
import { AssignmentsService, ConnectionsService, ShiftRateTypesEnum } from '../../../../../js/api';
import { useCurrencyFormatter } from '../../../../../js/app/hooks/use-currency-formatter';
import { handleApiError } from '../../../../../js/app/utils/errorhandling';
import { Button } from '../../../components/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../../components/table';
import { TooltippedText } from '../../../components/tooltipped-text';
import { StatusCategoryKinds, StatusCodes } from '../../../constants/workflows';
import { useCodeDefinitionsAsMap } from '../../../hooks/useCodeDefinitions';
import { useIsMobile } from '../../../hooks/useIsMobile';
import { formatDateTimeRange, formatDuration } from '../../../utils/date';
import { SHIFT_TYPE_ICON_COLORS, SHIFT_TYPE_ICONS } from '../../../utils/utils';
import { TIMECARD_QUERY_KEY } from './queryKeys';
import { ShiftDocumentsModal } from './ShiftDocumentsModal';
import { ShiftFormModal } from './ShiftFormModal';
import { ShiftHistoryModal } from './ShiftHistoryModal';
import { ShiftMobileCard } from './ShiftMobileCard';
import { ShiftRemoveModal } from './ShiftRemoveModal';

export const ShiftsTab: React.FC<{
  timecard: TimeCardDetail;
  canEdit?: boolean;
  onSuccess?: () => void;
}> = ({ timecard, canEdit = false, onSuccess }) => {
  const queryClient = useQueryClient();
  const isMobile = useIsMobile();
  const [editingShift, setEditingShift] = useState<TimeCardShift | undefined>();
  const [addingShift, setAddingShift] = useState(false);
  const [removingShift, setRemovingShift] = useState<TimeCardShift | undefined>();
  const [historyShift, setHistoryShift] = useState<TimeCardShift | undefined>();
  const [documentsShift, setDocumentsShift] = useState<TimeCardShift | undefined>();

  const { formatCurrencyPostfix } = useCurrencyFormatter({ postfix: '', placeholder: '-' });
  const { data: shiftTypeLabels = {} } = useCodeDefinitionsAsMap('ShiftRateTypes');
  const { data: removalReasonLabels = {} } = useCodeDefinitionsAsMap('RemovalReason');

  const statusAllowsEdit = useMemo(() => {
    return (
      timecard.current_status?.category_kind !== StatusCategoryKinds.FINAL &&
      (timecard.current_status?.code === StatusCodes.NEW ||
        timecard.current_status?.code === StatusCodes.INFO_NEEDED)
    );
  }, [timecard.current_status?.category_kind, timecard.current_status?.code]);

  // Fetch connected facilities for facility dropdown
  const effectiveProviderId = timecard.provider?.id;
  const { data: connectedFacilitiesData } = useQuery({
    queryKey: ['timecardFacilities', effectiveProviderId],
    queryFn: async () => {
      const response = await ConnectionsService.connectionsConnectedFacilityList({
        provider: effectiveProviderId,
        status: ['ACTIVE', 'AWAIT_PRVLG'] as any,
        limit: 100,
      });
      return response.results.map((cf) => ({
        id: cf.facility.id,
        name: cf.facility.name,
        city: cf.facility.city,
        state: cf.facility.state,
        shift_length: cf.shift_length,
        shift_start_day: cf.shift_start_day,
        shift_start_night: cf.shift_start_night,
        shift_start_24h: cf.shift_start_24h,
        // Rate types this connection offers. ConnectedFacilitySerializer
        // nulls one rate-set per audience (HCO sees `facility_rate_*`,
        // Provider sees `shift_rate_*`) so checking both is the audience-
        // agnostic way to ask "does this connection have a rate for X?".
        // Mirrors the backend's `allowed_shift_rate_types` model property.
        shift_rate_types: [
          cf.facility_rate_day || cf.shift_rate_day ? ShiftRateTypesEnum.DAY : null,
          cf.facility_rate_night || cf.shift_rate_night ? ShiftRateTypesEnum.NIGHT : null,
          cf.facility_rate_call || cf.shift_rate_call ? ShiftRateTypesEnum.CALL : null,
          cf.facility_rate_24h || cf.shift_rate_24h ? ShiftRateTypesEnum._24H : null,
        ].filter((t): t is ShiftRateTypesEnum => t !== null),
      }));
    },
    enabled: !!effectiveProviderId && canEdit,
  });

  const facilityOptions = useMemo(() => {
    if (!connectedFacilitiesData) return [];
    return connectedFacilitiesData
      .filter((f) => f.id)
      .map((f) => ({
        value: f.id!,
        label: f.name || 'Unnamed Facility',
        shift_length: f.shift_length,
        shift_start_day: f.shift_start_day,
        shift_start_night: f.shift_start_night,
        shift_start_24h: f.shift_start_24h,
        shift_rate_types: f.shift_rate_types,
      }));
  }, [connectedFacilitiesData]);

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: [TIMECARD_QUERY_KEY, timecard.id] });
    onSuccess?.();
  };

  // Create shift mutation
  const createMutation = useMutation({
    mutationFn: (data: Record<string, any>) =>
      AssignmentsService.assignmentsTimecardShiftCreate({
        requestBody: { ...(data as TimeCardShiftCreate), time_card: timecard.id },
      }),
    onSuccess: () => {
      toast.success('Shift added');
      setAddingShift(false);
      invalidate();
    },
    onError: (error) => handleApiError(error),
  });

  const editMutation = useMutation({
    mutationFn: ({ shiftId, data }: { shiftId: string; data: Record<string, any> }) =>
      AssignmentsService.assignmentsTimecardShiftPartialUpdate({
        id: shiftId,
        requestBody: data,
      }),
    onSuccess: () => {
      toast.success('Shift updated');
      setEditingShift(undefined);
      invalidate();
    },
    onError: (error) => handleApiError(error),
  });

  const removeMutation = useMutation({
    mutationFn: ({
      shiftId,
      data,
    }: {
      shiftId: string;
      data: { removal_reason: string; explanation?: string };
    }) =>
      AssignmentsService.assignmentsTimecardShiftRemovePartialUpdate({
        id: shiftId,
        requestBody: data,
      }),
    onSuccess: () => {
      toast.success('Shift removed');
      invalidate();
    },
    onError: (error) => handleApiError(error),
  });

  const restoreMutation = useMutation({
    mutationFn: (shiftId: string) =>
      AssignmentsService.assignmentsTimecardShiftRestorePartialUpdate({ id: shiftId }),
    onSuccess: () => {
      toast.success('Shift restored');
      invalidate();
    },
    onError: (error) => handleApiError(error),
  });

  const handleRemoveConfirm = (data: { removal_reason: string; explanation: string }) => {
    if (!removingShift) return;
    removeMutation.mutate(
      { shiftId: removingShift.id, data },
      { onSuccess: () => setRemovingShift(undefined) }
    );
  };

  const handleRestore = (shift: TimeCardShift) => {
    restoreMutation.mutate(shift.id);
  };

  const shifts = timecard.shifts || [];
  const primaryFacilityName = timecard.facility?.name;
  const uniqueFacilities = new Set(shifts.map((s) => s.facility?.id).filter(Boolean));
  const showFacility = uniqueFacilities.size > 1;
  const showBonus = shifts.some((s) => s.bonus_amount && Number(s.bonus_amount) > 0);
  const showHoliday = shifts.some((s) => s.holiday_amount && Number(s.holiday_amount) > 0);
  const showRemovalReason = shifts.some((s) => s.is_removed && s.removal_reason);
  const showExplanation = shifts.some((s) => s.explanation);

  return (
    <div className="tw:space-y-1">
      {canEdit && statusAllowsEdit && (
        <div className="tw:flex tw:justify-end">
          <Button small color="brand-green-outline" onClick={() => setAddingShift(true)}>
            <i className="fas fa-plus tw:mr-1" />
            Add Shift
          </Button>
        </div>
      )}

      {shifts.length === 0 ? (
        <div className="tw:text-center tw:text-zinc-500 tw:py-8">No shifts on this timecard</div>
      ) : isMobile ? (
        <div className="tw:flex tw:flex-col tw:gap-2 tw:mt-3">
          {shifts.map((shift) => (
            <ShiftMobileCard
              key={shift.id}
              shift={shift}
              canEdit={canEdit && statusAllowsEdit}
              showFacility={showFacility}
              primaryFacilityName={primaryFacilityName}
              shiftTypeLabels={shiftTypeLabels}
              removalReasonLabels={removalReasonLabels}
              onEdit={setEditingShift}
              onRemove={setRemovingShift}
              onRestore={handleRestore}
              onHistory={setHistoryShift}
              onDocuments={setDocumentsShift}
            />
          ))}
        </div>
      ) : (
        <Table compact striped>
          <TableHead>
            <TableHeader>
              <span />
            </TableHeader>
            <TableHeader>Date / Time</TableHeader>
            <TableHeader className="tw:!text-center">Type</TableHeader>
            <TableHeader className="tw:!text-center">Duration</TableHeader>
            {showBonus && <TableHeader className="tw:!text-right">Bonus</TableHeader>}
            {showHoliday && <TableHeader className="tw:!text-right">Holiday</TableHeader>}
            <TableHeader className="tw:!text-right">Amount</TableHeader>
            {showRemovalReason && <TableHeader>Reason</TableHeader>}
            {showExplanation && <TableHeader>Notes</TableHeader>}
          </TableHead>
          <TableBody>
            {/* eslint-disable-next-line sonarjs/cognitive-complexity */}
            {shifts.map((shift) => {
              const isRemoved = shift.is_removed;
              const modBadges: string[] = [];
              if (shift.is_added) modBadges.push('Added');
              if (shift.is_facility_changed) modBadges.push('Facility Changed');
              if (shift.is_extended) modBadges.push('Extended');
              if (shift.is_reduced) modBadges.push('Reduced');

              return (
                <TableRow key={shift.id}>
                  <TableCell>
                    <div className="tw:flex tw:items-center tw:gap-2">
                      <button
                        type="button"
                        className="tw:text-zinc-400 tw:hover:text-zinc-600 tw:cursor-pointer tw:bg-transparent tw:border-0 tw:p-0"
                        onClick={() => setHistoryShift(shift)}
                        title="View shift history"
                      >
                        <i className="fas fa-clock-rotate-left tw:text-sm" />
                      </button>
                      <button
                        type="button"
                        className="tw:text-zinc-400 tw:hover:text-zinc-600 tw:cursor-pointer tw:bg-transparent tw:border-0 tw:p-0 tw:relative"
                        onClick={() => setDocumentsShift(shift)}
                        title="Shift documents"
                      >
                        <i className="fas fa-paperclip tw:text-sm" />
                        {(shift.document_count ?? 0) > 0 && (
                          <span className="tw:absolute tw:-top-1.5 tw:-right-2 tw:bg-brand-green-0 tw:text-white tw:text-[9px] tw:leading-none tw:font-medium tw:rounded-full tw:min-w-[14px] tw:h-[14px] tw:flex tw:items-center tw:justify-center tw:px-0.5">
                            {shift.document_count}
                          </span>
                        )}
                      </button>
                      {canEdit && (
                        <button
                          type="button"
                          className="tw:text-zinc-400 tw:hover:text-brand-green-0 tw:cursor-pointer tw:bg-transparent tw:border-0 tw:p-0"
                          onClick={() => setEditingShift(shift)}
                          title="Edit shift"
                        >
                          <i className="fas fa-pencil tw:text-sm" />
                        </button>
                      )}
                      {canEdit && !isRemoved && (
                        <button
                          type="button"
                          className="tw:text-zinc-400 tw:hover:text-stoplight-danger tw:cursor-pointer tw:bg-transparent tw:border-0 tw:p-0"
                          onClick={() => setRemovingShift(shift)}
                          title="Remove shift"
                        >
                          <i className="fas fa-xmark tw:text-sm" />
                        </button>
                      )}
                      {canEdit && isRemoved && (
                        <button
                          type="button"
                          className="tw:text-zinc-400 tw:hover:text-brand-green-0 tw:cursor-pointer tw:bg-transparent tw:border-0 tw:p-0"
                          onClick={() => handleRestore(shift)}
                          title="Restore shift"
                        >
                          <i className="fas fa-rotate-left tw:text-sm" />
                        </button>
                      )}
                    </div>
                  </TableCell>
                  <TableCell
                    className={clsx(
                      'tw:whitespace-nowrap',
                      isRemoved && 'tw:opacity-50 tw:line-through'
                    )}
                  >
                    <div>{formatDateTimeRange(shift)}</div>
                    {showFacility && (
                      <div className="tw:text-[12px] tw:leading-tight tw:text-zinc-500">
                        {primaryFacilityName && shift.facility?.name !== primaryFacilityName && (
                          <i className="far fa-info-circle tw:mr-1 tw:text-blue-600" />
                        )}
                        {shift.facility?.name}
                      </div>
                    )}
                    {modBadges.length > 0 && (
                      <div className="tw:flex tw:gap-1 tw:mt-0.5">
                        {modBadges.map((badge) => (
                          <span
                            key={badge}
                            className="tw:text-[9px] tw:bg-amber-100 tw:text-amber-700 tw:px-1 tw:rounded"
                          >
                            {badge}
                          </span>
                        ))}
                      </div>
                    )}
                  </TableCell>
                  <TableCell className={clsx('tw:text-center', isRemoved && 'tw:opacity-50')}>
                    <div className="tw:flex tw:flex-col tw:items-center tw:leading-tight">
                      <span className="tw:[text-decoration:none]">
                        <i
                          className={`fas tw:text-lg ${SHIFT_TYPE_ICONS[shift.shift_rate_type as keyof typeof SHIFT_TYPE_ICONS] || ''} ${SHIFT_TYPE_ICON_COLORS[shift.shift_rate_type as keyof typeof SHIFT_TYPE_ICON_COLORS] || ''}`}
                        />
                      </span>
                      <span className="tw:text-[10px] tw:text-zinc-500">
                        {shiftTypeLabels[shift.shift_rate_type] || shift.shift_rate_type}
                      </span>
                    </div>
                  </TableCell>
                  <TableCell
                    className={clsx('tw:text-center', isRemoved && 'tw:opacity-50 tw:line-through')}
                  >
                    {shift.duration ? formatDuration(shift.duration) : '--'}
                  </TableCell>
                  {showBonus && (
                    <TableCell
                      className={clsx(
                        'tw:text-right',
                        isRemoved && 'tw:opacity-50 tw:line-through'
                      )}
                    >
                      {formatCurrencyPostfix(shift.bonus_amount)}
                    </TableCell>
                  )}
                  {showHoliday && (
                    <TableCell
                      className={clsx(
                        'tw:text-right',
                        isRemoved && 'tw:opacity-50 tw:line-through'
                      )}
                    >
                      {formatCurrencyPostfix(shift.holiday_amount)}
                    </TableCell>
                  )}
                  <TableCell
                    className={clsx('tw:text-right', isRemoved && 'tw:opacity-50 tw:line-through')}
                  >
                    {formatCurrencyPostfix(shift.total_amount)}
                  </TableCell>
                  {showRemovalReason && (
                    <TableCell className="tw:text-xs tw:text-zinc-500">
                      {shift.is_removed && shift.removal_reason
                        ? removalReasonLabels[shift.removal_reason] || shift.removal_reason
                        : null}
                    </TableCell>
                  )}
                  {showExplanation && (
                    <TableCell className="tw:text-xs tw:text-zinc-500">
                      {shift.explanation && shift.explanation.length > 30 ? (
                        <>
                          {shift.explanation.slice(0, 26)}...{' '}
                          <TooltippedText
                            tooltipContent={shift.explanation}
                            tooltipClassName="tw:max-w-[300px]"
                          >
                            <span className="tw:underline tw:cursor-default tw:text-blue-700">
                              more
                            </span>
                          </TooltippedText>
                        </>
                      ) : (
                        shift.explanation || ''
                      )}
                    </TableCell>
                  )}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}

      <ShiftFormModal
        open={!!editingShift}
        onClose={() => setEditingShift(undefined)}
        onSave={(shiftId, data) => editMutation.mutate({ shiftId, data })}
        mode="edit"
        shift={editingShift}
        facilityOptions={facilityOptions}
        isPending={editMutation.isLoading}
      />

      <ShiftFormModal
        open={addingShift}
        onClose={() => setAddingShift(false)}
        onSave={() => {}}
        onCreate={(data) => createMutation.mutate(data)}
        mode="add"
        facilityOptions={facilityOptions}
        isPending={createMutation.isLoading}
        timecardStartOn={timecard.start_on}
        timecardEndOn={timecard.end_on}
        existingShiftDates={shifts.map((s) => s.start_on)}
        defaultFacilityId={timecard.facility?.id}
      />

      <ShiftHistoryModal
        open={!!historyShift}
        onClose={() => setHistoryShift(undefined)}
        shift={historyShift}
      />

      <ShiftDocumentsModal
        open={!!documentsShift}
        onClose={() => {
          setDocumentsShift(undefined);
          invalidate();
        }}
        shift={documentsShift}
        ownerId={timecard.provider?.id || ''}
        relatedProviderId={timecard.provider?.id}
      />

      <ShiftRemoveModal
        open={!!removingShift}
        onClose={() => setRemovingShift(undefined)}
        onConfirm={handleRemoveConfirm}
        shift={removingShift}
        isPending={removeMutation.isLoading}
      />
    </div>
  );
};
